import { AutoSplitCandidate, MatchStatus } from "./auto_split_wizard_types"
import { OrgOption } from "@/api/org_options"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { SearchableSelect, SearchableSelectOption } from "@/components/searchable_select"
import { Check } from "lucide-react"
import { useMemo } from "preact/hooks"

interface MatchStats {
  rows: number
  amountCents: number
}

interface AutoSplitMappingPreviewProps {
  candidates: AutoSplitCandidate[]
  allCandidates: AutoSplitCandidate[]
  onUpdateCandidate: (candidateId: string, patch: Partial<AutoSplitCandidate>) => void
  retailerOptions: OrgOption[]
  productLineOptions: OrgOption[]
  productCategoryOptions: OrgOption[]
  autoMatchedStats: MatchStats
  enforcedTotalStats: MatchStats
}

const STATUS_COLORS: Record<MatchStatus, string> = {
  matched: "text-green-700 bg-green-50 border-green-200",
  fallback: "text-amber-700 bg-amber-50 border-amber-200",
  unmatched: "text-red-700 bg-red-50 border-red-200",
}

function statusLabel(status: MatchStatus): string {
  return status === "unmatched" ? "unmatched - needs review" : status
}

interface MappedFieldInputProps {
  label: string
  value: string
  options: SearchableSelectOption[]
  onUpdate: (value: string) => void
}

function MappedFieldInput({ label, value, options, onUpdate }: MappedFieldInputProps) {
  if (options.length > 0) {
    return (
      <div class="space-y-1">
        <Label class="text-xs">{label}</Label>
        <SearchableSelect
          options={options}
          value={value}
          onValueChange={onUpdate}
          placeholder={`Select ${label.toLowerCase()}...`}
          searchPlaceholder={`Search ${label.toLowerCase()}...`}
        />
      </div>
    )
  }
  return (
    <div class="space-y-1">
      <Label class="text-xs">{label}</Label>
      <Input
        value={value}
        onInput={(e: Event) => onUpdate((e.currentTarget as HTMLInputElement).value)}
      />
    </div>
  )
}

function toSelectOptions(orgOptions: OrgOption[]): SearchableSelectOption[] {
  return orgOptions.map((o: OrgOption) => ({ value: o.option_value, label: o.option_value }))
}

interface FieldConfig {
  label: string
  candidateKey: "retailer_name" | "product_line" | "product_category"
  statusKey: "retailer" | "product_line" | "product_category"
  rawLabel: string
  rawKey: "raw_retailer" | "raw_product_description" | "raw_upc_key"
}

const FIELD_CONFIGS: FieldConfig[] = [
  { label: "Mapped Retailer", candidateKey: "retailer_name", statusKey: "retailer", rawLabel: "Raw Retailer", rawKey: "raw_retailer" },
  { label: "Mapped Product Line", candidateKey: "product_line", statusKey: "product_line", rawLabel: "Raw Product", rawKey: "raw_product_description" },
  { label: "Mapped Product Group", candidateKey: "product_category", statusKey: "product_category", rawLabel: "UPC/Item Key", rawKey: "raw_upc_key" },
]

const STATUS_LABELS: Record<string, string> = {
  retailer: "Retailer",
  product_line: "Product Line",
  product_category: "Product Group",
}

function pct(count: number, total: number): string {
  return total === 0 ? "0" : Math.round((count / total) * 100).toString()
}

function sumMatchStats(candidates: AutoSplitCandidate[]): MatchStats {
  return candidates.reduce((acc: MatchStats, c: AutoSplitCandidate) => {
    const e = c.option_enforced
    const s = c.match_status
    if (!e.retailer && !e.product_line && !e.product_category) return acc
    const matched = !(
      (e.retailer && s.retailer === "unmatched") ||
      (e.product_line && s.product_line === "unmatched") ||
      (e.product_category && s.product_category === "unmatched")
    )
    return matched
      ? { rows: acc.rows + c.source_count, amountCents: acc.amountCents + c.source_amount_cents }
      : acc
  }, { rows: 0, amountCents: 0 })
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })
}

export function AutoSplitMappingPreview({
  candidates,
  allCandidates,
  onUpdateCandidate,
  retailerOptions,
  productLineOptions,
  productCategoryOptions,
  autoMatchedStats,
  enforcedTotalStats,
}: AutoSplitMappingPreviewProps) {
  const optionsByField = useMemo(() => ({
    retailer_name: toSelectOptions(retailerOptions),
    product_line: toSelectOptions(productLineOptions),
    product_category: toSelectOptions(productCategoryOptions),
  }), [retailerOptions, productLineOptions, productCategoryOptions])

  const currentStats = useMemo(() => sumMatchStats(allCandidates), [allCandidates])
  const totalRows = enforcedTotalStats.rows
  const totalAmount = enforcedTotalStats.amountCents

  return (
    <div class="space-y-2">
      {totalRows > 0 && (
        <div class="space-y-0.5 px-0.5 text-xs leading-snug text-gray-500">
          <div>
            Auto-matched: <span class="font-medium text-gray-700">{pct(autoMatchedStats.rows, totalRows)}%</span> rows ({autoMatchedStats.rows}/{totalRows}) · {formatDollars(autoMatchedStats.amountCents)} of {formatDollars(totalAmount)}
          </div>
          <div class="flex items-center gap-1">
            Total matched: <span class="font-medium text-gray-700">{pct(currentStats.rows, totalRows)}%</span> rows ({currentStats.rows}/{totalRows}) · {formatDollars(currentStats.amountCents)} of {formatDollars(totalAmount)}
            {currentStats.rows === totalRows && <Check className="h-3 w-3" strokeWidth={3} style={{ color: "#22c55e" }} />}
          </div>
        </div>
      )}
      {candidates.map((candidate: AutoSplitCandidate, index: number) => (
        <div key={candidate.id} class="rounded-md border border-gray-200 bg-white p-3">
          <div class="mb-2 flex items-center justify-between gap-2">
            <div class="text-sm font-semibold">Group {index + 1}</div>
            <div class="shrink-0 text-xs text-gray-600">
              {candidate.source_count} rows • {candidate.source_amount.toFixed(2)}
            </div>
          </div>

          <div class="mb-2 grid grid-cols-1 gap-2 text-xs leading-snug md:grid-cols-3">
            {FIELD_CONFIGS.map((field: FieldConfig) => (
              <div key={field.rawKey} class="min-w-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
                <div class="mb-0.5 font-medium text-gray-700">{field.rawLabel}</div>
                <div class="break-words">{candidate[field.rawKey] || "N/A"}</div>
              </div>
            ))}
          </div>

          <div class="mb-2 grid grid-cols-1 gap-2 md:grid-cols-3">
            {FIELD_CONFIGS.map((field: FieldConfig) => (
              <MappedFieldInput
                key={field.candidateKey}
                label={field.label}
                value={candidate[field.candidateKey]}
                options={optionsByField[field.candidateKey]}
                onUpdate={(val: string) => onUpdateCandidate(candidate.id, { [field.candidateKey]: val })}
              />
            ))}
          </div>

          <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
            {FIELD_CONFIGS.map((field: FieldConfig) => (
              <div key={field.statusKey} class="min-w-0">
                <span
                  class={`inline-block max-w-full rounded-md border px-2 py-0.5 text-[11px] leading-tight ${STATUS_COLORS[candidate.match_status[field.statusKey]]}`}
                >
                  {STATUS_LABELS[field.statusKey]}: {statusLabel(candidate.match_status[field.statusKey])}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
